'use client';

import type { RadioGroupProps } from '@heroui/react';
import { RadioGroup } from '@heroui/react';
import React from 'react';
import { useTranslation } from '@/providers/app-provider/app-provider';
import { cn } from '@/utils';
import { tFormat } from '@/utils/t-format';
import { RatingHoverContext } from '../rating-radio-item/rating-hover-context';
import RatingRadioItem from '../rating-radio-item/rating-radio-item';

type RatingRadioGroupProps = RadioGroupProps & {
  hideStarsText?: boolean;
};

const RatingRadioGroup = React.forwardRef<HTMLDivElement, RatingRadioGroupProps>(
  ({ className, label, hideStarsText, value: valueProp, onValueChange, ...props }, ref) => {
    const { t } = useTranslation();
    const [internalValue, setInternalValue] = React.useState('5');
    const [hoveredValue, setHoveredValue] = React.useState<string | null>(null);
    const isControlled = valueProp !== undefined;
    const value = isControlled ? (valueProp ?? '1') : internalValue;
    const setValue = (next: string) => {
      if (!isControlled) setInternalValue(next);
      onValueChange?.(next);
    };
    const displayedValue = hoveredValue ?? value;
    const starsText = React.useMemo(() => {
      // Special case for 5 stars
      if (displayedValue === '5') {
        return tFormat(t?.common.amountStars, { amount: displayedValue });
      }

      // For 1 to 4 stars, use a generic approach
      return tFormat(t?.common.amountStarsAndUp, { amount: displayedValue });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [displayedValue, t?.common.amountStarsAndUp, t?.common.amountStars]);

    return (
      <div className={cn('flex items-center gap-3', className)}>
        <RatingHoverContext.Provider value={{ hoveredValue, setHoveredValue }}>
          <RadioGroup
            ref={ref}
            value={value}
            {...props}
            defaultValue="5"
            orientation="horizontal"
            onValueChange={setValue}
          >
            <RatingRadioItem value="1" />
            <RatingRadioItem value="2" />
            <RatingRadioItem value="3" />
            <RatingRadioItem value="4" />
            <RatingRadioItem value="5" />
          </RadioGroup>
        </RatingHoverContext.Provider>
        {label ? label : null}
        {!hideStarsText && <p className="text-medium text-default-400">{starsText}</p>}
      </div>
    );
  },
);

RatingRadioGroup.displayName = 'RatingRadioGroup';

export default RatingRadioGroup;
